import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { DiaryRootState } from 'app/appRoot/rootReducer';
import BookingLookupService from 'external-services/booking-lookup-service';
import ServicesLookupService from 'external-services/services-lookup-service';
import BookingsUtilsService from 'external-services/bookings-utils-service';
import { IServiceViewItem } from 'external-services/booking-lookup-service/types';
import { bookingStatusType, IBooking } from 'shared-types/index';
import { IServiceQtyItem } from 'design-v2/common/ServiceQuantityOverview/types';
import EventsService from 'external-services/events-lookup-service';
import { errorStateActions, getAccordionDetail } from 'app/reducers/errorState/slice';
import { CONTACT_MSG, PROBLEMS_MSG, REFRESH_MSG } from 'appRoot/app-error-messages';

export interface IServiceQuantityState {
  serviceQtyList: IServiceQtyItem[];
}

interface IServiceQtyListState {
  list: IServiceQtyItem[];
}
export const initialState: IServiceQtyListState = {
  list: [],
};

// TODO: move to status service
const isBookingCancelled = (booking: IBooking): boolean => {
  return booking.status.statusType === 'cancelled';
};
const isBookingWaitList = (booking: IBooking): boolean => {
  return !booking.tables || booking.tables.length === 0;
};

export const loadServiceQtyList = createAsyncThunk(
  'serviceQtyList',
  async (_, { getState, dispatch }): Promise<IServiceQtyItem[]> => {
    try {
      const { scheduleReducer } = getState() as DiaryRootState;
      const selectedSchedule = scheduleReducer?.selectedSchedule;
      if (!selectedSchedule) return [];

      const { bookings } = await BookingLookupService.getBySchedule(selectedSchedule);
      const haveAtleastOneFunctionTrackerBooking =
        bookings?.some((booking) => booking.functionTracker === true) || false;
      const serviceViewList = await ServicesLookupService.getServicesAndServiceGroupsAsync(
        selectedSchedule
      );
      const bookingsByService = BookingLookupService.setBookingsByServiceObject(
        serviceViewList as any as IServiceViewItem[],
        haveAtleastOneFunctionTrackerBooking
      );

      // Extracts the key from the last item in the bookingsByService.
      const lastItemKey = Object.keys(bookingsByService).pop();
      const serviceQtyList: IServiceQtyItem[] = Object.values(
        bookings.reduce((result: any, booking) => {
          // Get serviceIndex for this booking
          const serviceIndex: any = booking.functionTracker
            ? lastItemKey
            : BookingsUtilsService.getServiceFromBooking(booking, serviceViewList as any);

          if (serviceIndex < 0) {
            // serviceIndex is not found then do nothing
            return result;
          }

          const bookingByService = result[serviceIndex];

          if (!bookingByService) {
            return result;
          }

          const isSeated = booking.status.statusType === bookingStatusType.seated;

          // wait list bookings shouldn't count towards totals
          const isNotCancelled = !isBookingCancelled(booking);
          const isNotWaitList = !isBookingWaitList(booking);
          const isNotPrivateFunEvent = !EventsService.isPrivateFunEvent(bookingByService.service);

          const isCoverCounted = isNotCancelled && isNotWaitList && isNotPrivateFunEvent;
          const isBookingCounted = isNotCancelled && isNotWaitList;
          return {
            ...result,
            [serviceIndex]: {
              bookings: isBookingCounted
                ? Number(bookingByService.bookings) + 1
                : Number(bookingByService.bookings),
              covers: isCoverCounted
                ? bookingByService.covers + booking.people
                : bookingByService.covers,
              name: bookingsByService[serviceIndex].service.name,
              seated: isSeated
                ? (bookingByService.seated || 0) + booking.people
                : bookingByService.seated || 0,
            },
          };
        }, bookingsByService)
      ).map((item: any) => {
        // Service has no booking yet
        const { service, covers, bookingsCount } = item;
        return service
          ? {
              bookings: bookingsCount,
              covers: covers,
              name: service.name,
              seated: 0,
            }
          : item;
      });
      return serviceQtyList;
    } catch (e) {
      console.error('serviceQtyList', e);
      dispatch(
        errorStateActions.setErrorState({
          errorTitle: 'Problem calculating services',
          errorMessage: `There was an error trying to calculate bookings and covers across your services. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
          accordionDetail: getAccordionDetail(e),
        })
      );
      return [];
    }
  }
);

const serviceQtyListSlice = createSlice({
  name: 'serviceQtyList', // used in action types
  initialState,
  reducers: {
    resetState: (state: IServiceQtyListState) => {
      state = initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadServiceQtyList.fulfilled, (state: IServiceQtyListState, action) => {
      state.list = action.payload;
    });
  },
});

// Extract the action creators object and the reducer
const { reducer, actions } = serviceQtyListSlice;
// Extract and export each action creator by name
export const { resetState } = actions;
// Export the reducer, either as a default or named export
export default reducer;
